/**
 * ChipInput — controlled list of string chips.
 *
 * Enter to add, Backspace on empty input removes last, click X removes by
 * index. Tab moves focus into chip strip; Delete removes focused chip.
 */

import { useRef, useState, type KeyboardEvent } from "react";
import { X } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

export interface ChipInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  variant?: "default" | "destructive";
  ariaLabel?: string;
  inputId?: string;
  maxItems?: number;
}

export function ChipInput({
  value,
  onChange,
  placeholder,
  variant = "default",
  ariaLabel,
  inputId,
  maxItems,
}: ChipInputProps) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = (raw: string) => {
    const next = raw.trim();
    if (!next) return;
    if (value.includes(next)) {
      setDraft("");
      return;
    }
    if (maxItems && value.length >= maxItems) return;
    onChange([...value, next]);
    setDraft("");
  };

  const removeAt = (idx: number) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && !draft && value.length > 0) {
      e.preventDefault();
      removeAt(value.length - 1);
    }
  };

  const chipKeyDown = (e: KeyboardEvent<HTMLButtonElement>, idx: number) => {
    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      removeAt(idx);
      inputRef.current?.focus();
    }
  };

  const chipBase =
    variant === "destructive"
      ? "border-destructive/30 bg-destructive/15 text-destructive hover:bg-destructive/15"
      : "border-border/60 bg-muted/40 text-foreground hover:bg-muted/60";

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-2 py-1.5 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0"
      aria-label={ariaLabel}
      data-testid="chip-input"
    >
      {value.map((chip, idx) => (
        <span
          key={`${chip}-${idx}`}
          className={cn(
            "inline-flex items-center gap-1 rounded-full border pl-3 pr-1 py-0.5 text-xs transition",
            chipBase,
          )}
          data-testid="chip"
        >
          <span>{chip}</span>
          <button
            type="button"
            aria-label={`Remover ${chip}`}
            onClick={(e) => {
              e.stopPropagation();
              removeAt(idx);
            }}
            onKeyDown={(e) => chipKeyDown(e, idx)}
            className={cn(
              "inline-flex h-5 w-5 items-center justify-center rounded-full transition",
              "hover:bg-destructive/20 hover:text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              "cursor-pointer",
            )}
            data-testid="chip-remove"
          >
            <X className="size-3.5" strokeWidth={2.5} />
          </button>
        </span>
      ))}
      <Input
        ref={inputRef}
        id={inputId}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => {
          if (draft.trim()) commit(draft);
        }}
        placeholder={placeholder}
        className="h-7 flex-1 border-0 bg-transparent px-1 py-0 text-sm shadow-none focus-visible:ring-0"
        aria-label={ariaLabel}
      />
    </div>
  );
}
